// ─────────────────────────────────────────────────────────────────────
// regles — LA PORTÉE D'UNE RÈGLE MÉTIER
//
// `business_rules.scope` existe depuis `001_init`, il est `NOT NULL` et
// il est indexé `(scope, active)`. Le dépôt sait filtrer dessus.
//
// Et `chargerRegles()` l'ignorait entièrement :
//
//   SELECT id, input_text FROM business_rules
//    WHERE active AND validated_at IS NOT NULL … LIMIT 60
//
// Toutes les règles partaient sur toutes les pièces. Une règle « sur
// l'inox, compte 20 % de cycle en plus » se serait appliquée à
// l'aluminium — et personne ne l'aurait vu, puisqu'un temps majoré de
// 20 % reste parfaitement plausible.
//
// Charles, 27 août : *« il faut qu'elle existe pour ce type de pièce »*.
//
// ── CE QU'EST UNE PORTÉE ──
//
// Une chaîne, écrite par un humain, de la forme `clef:valeur` :
//
//   global                 toutes les pièces
//   matiere:C45            les pièces de cette nuance
//   famille:AERA           les pièces dont la référence commence ainsi
//   forme:cylinder         les pièces de révolution
//
// Plusieurs critères se séparent par une virgule et se cumulent en ET :
// `matiere:C45,forme:cylinder`.
//
// ── UNE PORTÉE ILLISIBLE NE FAIT PAS DISPARAÎTRE LA RÈGLE ──
//
// C'est la décision qui compte ici. Une règle validée par un humain qui
// cesserait de s'appliquer à cause d'une faute de frappe serait un
// échec SILENCIEUX : la courbe d'apprentissage resterait plate et
// personne ne saurait pourquoi. Une règle trop large, elle, se voit —
// le deviseur lit un temps qui ne colle pas et corrige.
//
// Une portée qu'on ne sait pas lire s'applique donc, et elle est
// SIGNALÉE comme telle. On préfère un bruit visible à un silence.
// ─────────────────────────────────────────────────────────────────────

/** Les clés comprises. Tout le reste rend la portée illisible.
 *  `client`, `operation`, `machine`, `qte_min` et `qte_max` : 14 octobre 2026
 *  (docs/OUTIL-ASSISTANT.md §4.2), comme chez AGC. */
export const CLEFS_PORTEE = ['global', 'matiere', 'famille', 'forme', 'client', 'operation', 'machine', 'qte_min', 'qte_max'];

/**
 * Les agents qui peuvent recevoir une règle, et leur famille (§5).
 * Une règle de PERCEPTION ne s'active qu'après passage du banc (étape 4,
 * voir `AGENTS_PERCEPTION_MESURABLES`). Le jugement, lui, reçoit tout —
 * c'est sa raison d'être (CLAUDE.md §2bis).
 */
export const AGENTS_REGLES = {
  estimation: 'jugement',
  decoupage_consignes: 'jugement',
  analyste: 'jugement',
  structure: 'jugement',
  tri_pages: 'perception',
  lecture_plan: 'perception',
  enveloppe: 'perception',
  lecture_courrier: 'perception',
  lecture_fiche: 'perception',
  recherche_matiere: 'perception',
};
export const AGENTS_JUGEMENT = Object.keys(AGENTS_REGLES).filter((a) => AGENTS_REGLES[a] === 'jugement');

// ── LES AGENTS DE LECTURE QUI ONT UN BANC — 15 octobre 2026 ──
//
// Une règle de lecture ne s'active qu'après un banc avec et sans elle,
// contre une vérité RELEVÉE À L'ŒIL (invariant 16). Trois agents en ont
// une ; les autres n'en ont pas, et une règle qui les vise est REFUSÉE
// (décision de Thibaud) : la lecture du plan, la fiche d'atelier et la
// recherche matière s'ouvriront le jour où leur vérité existera.
//
// Et la portée y est RESTREINTE à ce que l'agent connaît AVANT de lire :
// la matière, la forme ou la famille d'une pièce sortent de la lecture,
// donc une règle « sur l'inox » ne concernerait jamais l'agent qui lit —
// un critère non renseigné n'est pas satisfait. Le client est connu par
// le devis ; le courrier, lui, arrive avant tout client.
export const AGENTS_PERCEPTION_MESURABLES = {
  enveloppe: { clefs: ['client'], verite: 'deploy/verite-encombrement.json — 5 plans relevés à l’œil' },
  tri_pages: { clefs: ['client'], verite: 'les pages qui ont produit une pièce : aucune ne doit être écartée' },
  lecture_courrier: { clefs: [], verite: 'les 7 cas de deploy/banc-mail.mjs, dont 2 injections' },
};

/**
 * Pourquoi une règle de lecture ne peut PAS être proposée telle quelle —
 * ou `null` si elle le peut. Fonction pure, appelée par l'assistant et
 * par les tests.
 */
export function refusPerception(agents = [], portee = 'global') {
  const perception = agents.filter((a) => AGENTS_REGLES[a] === 'perception');
  if (!perception.length) return null;
  if (perception.length !== agents.length) {
    return 'Une règle vise soit des agents de lecture, soit des agents de jugement, pas les deux : '
      + 'les premiers exigent un banc, les seconds non. Proposez deux règles.';
  }
  const sansBanc = perception.filter((a) => !AGENTS_PERCEPTION_MESURABLES[a]);
  if (sansBanc.length) {
    return `Pas de règle pour ${sansBanc.join(', ')} : il n’existe aucune vérité relevée à l’œil pour mesurer `
      + 'cet agent avec et sans la règle. Pour une valeur lue de travers, la bonne voie est un alias '
      + 'ou une correction APRÈS la lecture.';
  }
  const { criteres, lisible } = lirePortee(portee);
  if (!lisible) return null; // la portée illisible a son propre refus
  for (const a of perception) {
    const permises = AGENTS_PERCEPTION_MESURABLES[a].clefs;
    const interdite = criteres.find((c) => !permises.includes(c.clef));
    if (interdite) {
      return `L’agent ${a} ne connaît pas « ${interdite.clef} » au moment de lire : `
        + (permises.length ? `sa portée ne peut être que global ou ${permises.join(', ')}.` : 'sa portée ne peut être que global.');
    }
  }
  return null;
}

import { familleDepuisDesignation } from './matiere.js';

const norm = (v) => String(v ?? '').trim().toLowerCase();

/**
 * Décompose une portée en critères.
 *
 * @returns {{criteres: Array<{clef: string, valeur: string}>, lisible: boolean}}
 */
export function lirePortee(scope) {
  const brut = norm(scope);
  if (!brut || brut === 'global' || brut === 'tous' || brut === 'toutes') {
    return { criteres: [], lisible: true };
  }
  const criteres = [];
  let lisible = true;
  for (const morceau of brut.split(',')) {
    const m = morceau.trim();
    if (!m) continue;
    const i = m.indexOf(':');
    const clef = i === -1 ? m : m.slice(0, i).trim();
    const valeur = i === -1 ? '' : m.slice(i + 1).trim();
    if (clef === 'global') continue;
    if (!CLEFS_PORTEE.includes(clef) || !valeur) { lisible = false; continue; }
    if ((clef === 'qte_min' || clef === 'qte_max') && !Number.isFinite(Number(valeur.replace(',', '.')))) {
      lisible = false;
      continue;
    }
    criteres.push({ clef, valeur });
  }
  return { criteres, lisible };
}

/**
 * Cette règle concerne-t-elle cette pièce ?
 *
 * @param {string} scope
 * @param {{matiere?: string|null, famille?: string|null, forme?: string|null}} piece
 * @returns {{applique: boolean, raison: string}}
 */
export function porteeCouvre(scope, piece = {}) {
  const { criteres, lisible } = lirePortee(scope);
  if (!lisible) {
    return { applique: true, raison: 'portée illisible — appliquée par défaut, à corriger' };
  }
  if (!criteres.length) return { applique: true, raison: 'global' };

  const valeurDe = {
    matiere: norm(piece.matiere),
    famille: norm(piece.famille),
    forme: norm(piece.forme),
  };
  const qte = Number(piece.qte);
  const liste = (v) => (Array.isArray(v) ? v : []).map(norm).filter(Boolean);
  // Un critère que la pièce ne renseigne pas n'est pas satisfait : on ne
  // suppose pas qu'une règle sur l'inox concerne une pièce dont personne
  // n'a dit la matière.
  const satisfait = (c) => {
    const attendu = norm(c.valeur);
    if (c.clef === 'qte_min' || c.clef === 'qte_max') {
      if (!Number.isFinite(qte) || piece.qte == null) return false;
      const borne = Number(attendu.replace(',', '.'));
      return c.clef === 'qte_min' ? qte >= borne : qte <= borne;
    }
    // Le client : « precicarb » couvre « PRECICARB S.A » — le nom au
    // fichier porte souvent sa forme juridique.
    if (c.clef === 'client') return Boolean(norm(piece.client)) && norm(piece.client).includes(attendu);
    // La matière : le libellé exact, un libellé qui le CONTIENT (« inox »
    // couvre « Inox 316L »), ou la FAMILLE de la nuance (« inox » couvre
    // « X2CrNiMo17-12-2 »). L'égalité stricte d'avant le 14 octobre 2026
    // faisait couvrir 1 pièce sur 13 à une règle « sur l'inox » — vu par la
    // simulation du premier essai réel, pas par un test.
    if (c.clef === 'matiere') {
      const lu = norm(piece.matiere);
      if (!lu) return false;
      return lu === attendu || lu.includes(attendu) || norm(familleDepuisDesignation(piece.matiere)) === attendu;
    }
    if (c.clef === 'operation') return liste(piece.operations).includes(attendu);
    if (c.clef === 'machine') return liste(piece.machines).includes(attendu);
    const v = valeurDe[c.clef];
    return Boolean(v) && v === attendu;
  };
  for (const c of criteres) {
    if (!satisfait(c)) return { applique: false, raison: `${c.clef} ≠ ${c.valeur}` };
  }
  return { applique: true, raison: criteres.map((c) => `${c.clef}=${c.valeur}`).join(' et ') };
}

/** Filtre une liste de règles pour une pièce. Fonction pure. */
export function reglesPourLaPiece(regles = [], piece = {}) {
  return (regles ?? []).filter((r) => porteeCouvre(r?.scope, piece).applique);
}

/**
 * Les règles d'UN agent, pour UN contexte (§5) : validées, actives, non
 * remplacées — c'est au chargement de le garantir —, qui visent cet agent
 * et dont la portée couvre le contexte. Fonction pure.
 *
 * Une règle sans `agents` (écrite avant le 14 octobre) visait la seule
 * estimation, seule à lire les règles : elle y reste.
 */
export function reglesPourAgent(regles = [], agent, contexte = {}) {
  return (regles ?? []).filter((r) => {
    const cibles = Array.isArray(r?.agents) && r.agents.length ? r.agents : ['estimation'];
    return cibles.includes(agent) && porteeCouvre(r?.scope, contexte).applique;
  });
}
